/**
 * Clearance helpers — the bridge between Mapbox's metric vehicle envelope and
 * the feet/inches + pounds that tour managers actually think in.
 */

const METERS_PER_FOOT = 0.3048
const POUNDS_PER_TONNE = 2204.62

/** Accepted range for a custom height override: 6 ft to 15 ft. */
export const HEIGHT_MIN_M = 1.8
export const HEIGHT_MAX_M = 4.6

/**
 * Parse a height the way people write it: `13'6"`, `13' 6`, `13 6`,
 * `13ft 6in`, `13.5`, or `13′6″`. Returns meters, or null when the input
 * isn't a height inside [HEIGHT_MIN_M, HEIGHT_MAX_M].
 */
export function parseHeightFeet(raw: string): number | null {
	const text = raw.trim().toLowerCase().replace(/[′’]/g, "'").replace(/[″”]/g, '"')
	const match = /^(\d+(?:\.\d+)?)\s*(?:'|ft|feet)?\s*(?:(\d+(?:\.\d+)?)\s*(?:"|in|inches)?)?$/.exec(
		text,
	)
	if (!match) return null
	const feet = Number(match[1])
	const inches = match[2] === undefined ? 0 : Number(match[2])
	if (!Number.isFinite(feet) || inches >= 12) return null
	const meters = (feet + inches / 12) * METERS_PER_FOOT
	return meters >= HEIGHT_MIN_M && meters <= HEIGHT_MAX_M ? meters : null
}

/** Format meters as feet and inches, rounded to the nearest inch: 12′6″. */
export function formatHeightFeet(meters: number): string {
	const totalInches = Math.round(meters / METERS_PER_FOOT / (1 / 12))
	return `${Math.floor(totalInches / 12)}′${totalInches % 12}″`
}

/** Format metric tonnes as pounds, rounded to the nearest 500: 54,500 lb. */
export function formatWeightPounds(tonnes: number): string {
	const pounds = Math.round((tonnes * POUNDS_PER_TONNE) / 500) * 500
	return `${pounds.toLocaleString('en-US')} lb`
}
